/**
 * Invoice Print Utilities
 * A5 and A4 Print Formats
 */

import invoiceCalculationService from '../services/InvoiceCalculationService';
import businessInfoService from '../services/businessInfoService';
import paymentService from '../services/paymentService';
import { buildGroupedInvoiceProductRows } from './consolidatedInvoiceView';
import { resolveInvoiceSupplier, resolveBusinessInfoForDocument } from './resolveInvoiceSupplier';
import { assertCanPrintReceipt } from './workerPermissions';
import {
  readStoredSessionUser,
  readStoredCurrentBusiness
} from '../services/offlineAuthSession';
import { formatSafeDate } from './formatSafety';
import { printHtmlDocument } from './printHtmlDocument';
import { buildPrintAdsCssVariables, buildPrintAdsInkForceCss } from './printAdsPalette';
import { getInvoicePrintLabel } from './i18n/invoicePrint';
import {
  getActiveCurrencyLabel,
  readActiveCurrencyType,
  toDisplayAmount
} from './posCurrencyUtils';
import { resolveInvoiceRemainingAmount } from '../domain/suppliers/balance';

const BUSINESS_INFO_CACHE_TTL_MS = 60_000;
let businessInfoCache = { id: null, data: null, loadedAt: 0 };

const formatCurrency = (amount, isRTL = false) => {
  const language = isRTL ? 'ar' : 'fr';
  const currencyType = readActiveCurrencyType();
  const label = getActiveCurrencyLabel(language, { form: 'short' });
  const display = toDisplayAmount(Number(amount) || 0, currencyType);
  const locale = isRTL ? 'ar-MR-u-nu-latn' : 'fr-MR';
  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(display);
    return `${formatted} ${label}`;
  } catch {
    return `${display.toFixed(2)} ${label}`;
  }
};

const escapeHtml = (text) =>
  String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const L = (key, isRTL) => getInvoicePrintLabel(key, isRTL);

function buildPrintItemRowHtml(item, isRTL) {
  const name = escapeHtml(item.productName || item.name || L('article', isRTL));
  const sku = escapeHtml(item.sku || '—');
  const qty = item.quantity || 1;
  const unit = item.unitPrice || item.price || 0;
  const total = item.totalPrice || qty * unit;
  return `
    <tr>
      <td>${name}</td>
      <td style="text-align: center; direction: ltr;">${sku}</td>
      <td style="text-align: center;">${qty}</td>
      <td style="text-align: end; direction: ltr;">${formatCurrency(unit, isRTL)}</td>
      <td style="text-align: end; direction: ltr;">${formatCurrency(total, isRTL)}</td>
    </tr>`;
}

function buildPrintTableBodyHtml(invoice, items, isRTL, allInvoices = []) {
  const groupedRows = buildGroupedInvoiceProductRows(invoice, allInvoices, isRTL);
  if (groupedRows) {
    return groupedRows
      .map((row) => {
        if (row.type === 'header') {
          return `<tr class="group-header"><td colspan="5"><strong>${escapeHtml(row.label)}</strong></td></tr>`;
        }
        return buildPrintItemRowHtml(row.item, isRTL);
      })
      .join('');
  }
  return items.map((item) => buildPrintItemRowHtml(item, isRTL)).join('');
}

function buildConsolidatedSummaryHtml(invoice, allInvoices, isRTL) {
  if (!paymentService.isConsolidatedInvoice(invoice)) return '';
  const childRows = paymentService.getConsolidatedChildRows(invoice, allInvoices || []);
  if (!childRows.length) return '';

  const title = L('consolidatedTitle', isRTL);
  const cols = [
    L('colInvoiceNumber', isRTL),
    L('colDate', isRTL),
    L('colTotal', isRTL),
    L('colRemaining', isRTL),
  ];
  const locale = isRTL ? 'ar-MR' : 'fr-MR';

  const rows = childRows
    .map((row) => {
      const date = formatSafeDate(
        row.invoiceDate,
        locale,
        { year: 'numeric', month: '2-digit', day: '2-digit' },
        '-'
      );
      return `
        <tr>
          <td>${escapeHtml(row.invoiceNumber)}</td>
          <td>${escapeHtml(date)}</td>
          <td style="direction: ltr; text-align: end;">${formatCurrency(row.totalAmount, isRTL)}</td>
          <td style="direction: ltr; text-align: end;">${formatCurrency(row.remainingAmount, isRTL)}</td>
        </tr>`;
    })
    .join('');

  return `
    <div class="consolidated-summary">
      <h3>${title}</h3>
      <table class="consolidated-summary-table">
        <thead><tr>${cols.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

/** Load current business branding for documents (export, print, invoices). */
export const loadBusinessInfo = async (forceRefresh = false) => {
  try {
    const businessFromUser = readStoredSessionUser()?.business || null;
    const businessFromStorage = readStoredCurrentBusiness();

    const businessId =
      businessFromUser?.id ||
      businessFromStorage?.id ||
      businessFromUser?.businessId ||
      businessFromStorage?.businessId ||
      null;

    const now = Date.now();
    if (
      !forceRefresh &&
      businessId &&
      businessInfoCache.id === businessId &&
      businessInfoCache.data &&
      now - businessInfoCache.loadedAt < BUSINESS_INFO_CACHE_TTL_MS
    ) {
      return businessInfoCache.data;
    }

    let businessInfoData = null;
    if (businessId) {
      try {
        businessInfoData = await businessInfoService.getBusinessInfo(businessId);
      } catch (error) {
        console.warn('Error loading from businessInfoService:', error);
      }
    }

    const result = {
      name:
        businessInfoData?.name ||
        businessFromStorage?.business_name ||
        businessFromStorage?.name ||
        businessFromUser?.business_name ||
        businessFromUser?.name ||
        'AMAN',
      address:
        businessInfoData?.address ||
        businessFromStorage?.address ||
        businessFromUser?.address ||
        'Adresse du magasin',
      phone:
        businessInfoData?.phone ||
        businessFromStorage?.phone ||
        businessFromUser?.phone ||
        'Téléphone',
      email:
        businessInfoData?.email ||
        businessFromStorage?.email ||
        businessFromUser?.email ||
        'email@example.com',
      logo: businessInfoData?.logo || businessFromStorage?.logo || businessFromUser?.logo || null,
      owner_name:
        businessInfoData?.owner_name ||
        businessFromStorage?.owner_name ||
        businessFromUser?.owner_name ||
        null,
      nif: businessInfoData?.nif || businessFromStorage?.nif || businessFromUser?.nif || null
    };

    businessInfoCache = { id: businessId, data: result, loadedAt: now };
    return result;
  } catch (error) {
    console.error('Error loading business info:', error);
    return {
      name: 'AMAN',
      address: 'Adresse du magasin',
      phone: 'Téléphone',
      email: 'email@example.com',
      logo: null,
      owner_name: null,
      nif: null
    };
  }
};

/**
 * A5 für normale Rechnungen, A4 für konsolidierte oder lange Listen.
 * @param {Object} invoice
 * @param {{ itemCount?: number, isConsolidated?: boolean }} [options]
 * @returns {'A4'|'A5'}
 */
export function resolveInvoicePrintFormat(invoice, options = {}) {
  const isConsolidated =
    options.isConsolidated ?? paymentService.isConsolidatedInvoice(invoice);
  const itemCount =
    options.itemCount ??
    (Array.isArray(invoice?.items) ? invoice.items.length : null) ??
    invoiceCalculationService.calculateInvoiceItems(invoice, false).length;
  return isConsolidated || itemCount > 10 ? 'A4' : 'A5';
}

/**
 * Einheitlicher Einstieg für Lieferantenrechnungen (Berechtigung + Format + Druck).
 * @param {Object} invoice
 * @param {{ format?: 'A4'|'A5', isRTL?: boolean, currentRole?: object, lang?: string }} [options]
 */
export async function printInvoiceDocument(invoice, options = {}) {
  const { isRTL = false, currentRole, lang = 'fr' } = options;
  if (currentRole) {
    assertCanPrintReceipt(currentRole, lang);
  }
  // Warm document print window + printer while we build HTML
  try {
    const { warmSilentPrintPipeline } = await import('./printerUtils');
    void warmSilentPrintPipeline({ kind: 'document' });
  } catch {
    /* optional */
  }
  const format = options.format || resolveInvoicePrintFormat(invoice);
  const printResult = await printInvoice(invoice, format, isRTL, {
    supplier: options.supplier || null,
    allInvoices: options.supermarketInvoices || [],
    businessInfo: options.businessInfo || null
  });
  return {
    success: Boolean(printResult?.success),
    format,
    error: printResult?.error || null,
    message: printResult?.message || null,
    connected: printResult?.connected,
    attempts: printResult?.attempts
  };
}

/**
 * Build supplier invoice HTML (A4/A5) without opening a print window.
 */
export function buildInvoicePrintHtml(
  invoice,
  format = 'A5',
  isRTL = false,
  printOptions = {}
) {
  const opts =
    printOptions && typeof printOptions === 'object' && !Array.isArray(printOptions)
      ? printOptions
      : { supplier: printOptions };
  const supplierDetails = opts.supplier ?? null;
  const allInvoices = opts.allInvoices ?? [];
  const businessInfo = opts.businessInfo || {
    name: 'AMAN',
    address: 'Adresse du magasin',
    phone: 'Téléphone',
    email: 'email@example.com',
    nif: null
  };

  const items = invoiceCalculationService.calculateInvoiceItems(invoice, isRTL);
  const totals = invoiceCalculationService.calculateInvoiceTotals(invoice);

  const printTotals = {
    total: totals.total || invoice.totalAmount || 0,
    paid: totals.paidAmount || invoice.paidAmount || 0,
    remaining:
      Number(totals.remainingAmount) || resolveInvoiceRemainingAmount(invoice)
  };

  const invoiceDateLabel = formatSafeDate(
    invoice.invoiceDate || invoice.createdAt,
    isRTL ? 'ar-MR' : 'fr-MR',
    { year: 'numeric', month: '2-digit', day: '2-digit' }
  );

  const pageSize = format === 'A4' ? 'A4' : 'A5';

  const supplier = resolveInvoiceSupplier(invoice, [], {
    fallbackName: '',
    override: supplierDetails
  });

  const docBusiness = resolveBusinessInfoForDocument(businessInfo, isRTL);
  const isConsolidated = paymentService.isConsolidatedInvoice(invoice);
  const invoiceTypeLabel = isConsolidated
    ? L('typeConsolidated', isRTL)
    : invoice.originalTransactionType === 'order'
      ? L('typeOrder', isRTL)
      : invoice.originalTransactionType === 'purchase'
        ? L('typePurchase', isRTL)
        : L('typeNormal', isRTL);

  const supplierRows = [
    `<div class="inv-doc-row inv-doc-row--card"><span class="inv-doc-row__label">${L('supplierName', isRTL)}</span><span class="inv-doc-row__value">${supplier.name || '—'}</span></div>`,
    `<div class="inv-doc-row inv-doc-row--card"><span class="inv-doc-row__label">${L('supplierAddress', isRTL)}</span><span class="inv-doc-row__value">${supplier.address || '—'}</span></div>`,
    `<div class="inv-doc-row inv-doc-row--card"><span class="inv-doc-row__label">${L('supplierPhone', isRTL)}</span><span class="inv-doc-row__value inv-doc-row__value--ltr">${supplier.phone || '—'}</span></div>`
  ].join('');

  const consolidatedSummaryHtml = buildConsolidatedSummaryHtml(invoice, allInvoices, isRTL);
  const tableBodyHtml = buildPrintTableBodyHtml(invoice, items, isRTL, allInvoices);

  return `<!DOCTYPE html>
    <html dir="${isRTL ? 'rtl' : 'ltr'}">
    <head>
      <meta charset="UTF-8">
      <title>Invoice ${invoice.invoiceNumber || invoice.id}</title>
      <style>
        ${buildPrintAdsCssVariables()}
        ${buildPrintAdsInkForceCss()}
        @page {
          size: ${pageSize};
          margin: 10mm;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: ${format === 'A4' ? '12px' : '10px'};
          direction: ${isRTL ? 'rtl' : 'ltr'};
          padding: 10mm;
        }
        .invoice-header {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(180px, 220px);
          gap: 16px;
          align-items: start;
          margin-bottom: 16px;
          border-bottom: 2px solid var(--print-text);
          padding-bottom: 10px;
        }
        .company-info,
        .invoice-meta {
          min-width: 0;
        }
        .company-info h2 {
          margin: 0 0 8px 0;
          font-size: ${format === 'A4' ? '18px' : '15px'};
          text-align: start;
        }
        .invoice-meta {
          border: 1px solid var(--print-secondary-200);
          border-radius: 6px;
          padding: 10px;
        }
        .invoice-meta-title {
          margin: 0 0 8px 0;
          font-size: ${format === 'A4' ? '16px' : '14px'};
          font-weight: 700;
          letter-spacing: 0.04em;
          text-align: start;
        }
        .inv-doc-rows {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .inv-doc-row {
          display: grid;
          grid-template-columns: minmax(72px, auto) minmax(0, 1fr);
          gap: 6px 10px;
          align-items: baseline;
        }
        .inv-doc-row--plain {
          grid-template-columns: 1fr;
        }
        .inv-doc-row--card {
          display: grid;
          grid-template-columns: minmax(72px, auto) minmax(0, 1fr);
          gap: 6px 10px;
          padding: 8px 10px;
          border: 1px solid var(--print-secondary-200);
          border-radius: 6px;
          background: var(--print-surface);
        }
        .inv-doc-row__label {
          font-weight: 600;
          color: var(--print-text-muted);
        }
        .inv-doc-row__value {
          font-weight: 600;
          word-break: break-word;
          text-align: start;
        }
        .inv-doc-row__value--ltr {
          direction: ltr;
          unicode-bidi: isolate;
        }
        .supplier-info {
          margin: 12px 0 16px;
          padding: 10px 12px;
          background: var(--print-surface-sunken);
          border-radius: 6px;
        }
        .supplier-info h3 {
          margin: 0 0 8px 0;
          font-size: ${format === 'A4' ? '13px' : '11px'};
          text-align: start;
        }
        .supplier-info .inv-doc-rows {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 8px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        th, td {
          border: 1px solid var(--print-secondary-200);
          padding: 8px;
          text-align: start;
        }
        th {
          background: var(--print-secondary-100);
          font-weight: bold;
        }
        tr.group-header td {
          background: var(--print-primary-subtle);
          font-weight: 700;
        }
        .consolidated-summary {
          margin: 12px 0 16px;
          padding: 10px 12px;
          background: var(--print-background);
          border: 1px solid var(--print-border-subtle);
          border-radius: 6px;
        }
        .consolidated-summary h3 {
          margin: 0 0 8px 0;
          font-size: ${format === 'A4' ? '13px' : '11px'};
          text-align: start;
        }
        .consolidated-summary-table {
          margin: 0;
        }
        .consolidated-summary-table th,
        .consolidated-summary-table td {
          font-size: ${format === 'A4' ? '11px' : '9px'};
          padding: 6px;
        }
        .totals {
          margin-top: 20px;
          text-align: end;
        }
        .totals-row {
          display: flex;
          justify-content: flex-end;
          margin: 5px 0;
        }
        .totals-label {
          width: 150px;
          font-weight: bold;
        }
        .totals-value {
          width: 120px;
          text-align: end;
          direction: ltr;
          unicode-bidi: isolate;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="invoice-header">
        <div class="company-info">
          <h2>${docBusiness.name}</h2>
          <div class="inv-doc-rows">
            <div class="inv-doc-row inv-doc-row--plain">
              <span class="inv-doc-row__value">${docBusiness.address}</span>
            </div>
            <div class="inv-doc-row inv-doc-row--plain">
              <span class="inv-doc-row__value">${docBusiness.phone}${docBusiness.email ? ' • ' + docBusiness.email : ''}</span>
            </div>
            ${docBusiness.nif ? `<div class="inv-doc-row inv-doc-row--plain"><span class="inv-doc-row__value">NIF: ${docBusiness.nif}</span></div>` : ''}
          </div>
        </div>
        <div class="invoice-meta">
          <h3 class="invoice-meta-title">${L('docTitle', isRTL)}</h3>
          <div class="inv-doc-rows">
            <div class="inv-doc-row">
              <span class="inv-doc-row__label">${L('invoiceNumber', isRTL)}</span>
              <span class="inv-doc-row__value">${invoice.invoiceNumber || invoice.id}</span>
            </div>
            <div class="inv-doc-row">
              <span class="inv-doc-row__label">${L('date', isRTL)}</span>
              <span class="inv-doc-row__value">${invoiceDateLabel}</span>
            </div>
            <div class="inv-doc-row">
              <span class="inv-doc-row__label">${L('type', isRTL)}</span>
              <span class="inv-doc-row__value">${invoiceTypeLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="supplier-info">
        <h3>${L('supplierInfo', isRTL)}</h3>
        <div class="inv-doc-rows">
          ${supplierRows}
        </div>
      </div>

      ${consolidatedSummaryHtml}

      <table>
        <thead>
          <tr>
            <th>${L('colProduct', isRTL)}</th>
            <th>${L('colSku', isRTL)}</th>
            <th>${L('colQty', isRTL)}</th>
            <th>${L('colUnitPrice', isRTL)}</th>
            <th>${L('colTotal', isRTL)}</th>
          </tr>
        </thead>
        <tbody>
          ${tableBodyHtml}
        </tbody>
      </table>

      <div class="totals">
        <div class="totals-row">
          <span class="totals-label">${L('total', isRTL)}:</span>
          <span class="totals-value" style="direction: ltr;">${formatCurrency(printTotals.total, isRTL)}</span>
        </div>
        <div class="totals-row">
          <span class="totals-label">${L('paid', isRTL)}:</span>
          <span class="totals-value" style="direction: ltr;">${formatCurrency(printTotals.paid, isRTL)}</span>
        </div>
        <div class="totals-row">
          <span class="totals-label">${L('remaining', isRTL)}:</span>
          <span class="totals-value" style="direction: ltr;">${formatCurrency(printTotals.remaining, isRTL)}</span>
        </div>
      </div>
    </body>
    </html>`;
}

export const printInvoice = async (invoice, format = 'A5', isRTL = false, printOptions = {}) => {
  const opts =
    printOptions && typeof printOptions === 'object' && !Array.isArray(printOptions)
      ? printOptions
      : { supplier: printOptions };

  const businessInfo = opts.businessInfo || (await loadBusinessInfo());
  const html = buildInvoicePrintHtml(invoice, format, isRTL, {
    ...opts,
    businessInfo
  });

  return printHtmlDocument(html, {
    mode: 'silent',
    kind: 'document',
    timeoutMs: format === 'A4' ? 12000 : 10000,
    retries: 1,
    imageWaitMs: 80,
    // @page { margin: 10mm } in HTML — avoid Chromium default margins doubling inset.
    margins: { marginType: 'none' },
    windowFeatures: 'width=900,height=700'
  });
};
